import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { LineReveal, ClipReveal } from "../motion/Reveal";
import { mediaUrl } from "@/lib/api";
import { optimizeUrl, buildSrcSet } from "@/lib/img";
import { ArrowUpRight } from "lucide-react";
import { useLang, UI } from "@/lib/lang";

export default function Gallery({ gallery = [] }) {
  const { t } = useLang();

  // Preload the first two images as soon as the section is used
  useEffect(() => {
    const links = [];
    (gallery || []).slice(0, 2).forEach((g) => {
      if (!g?.url) return;
      const l = document.createElement("link");
      l.rel = "preload";
      l.as = "image";
      l.href = optimizeUrl(mediaUrl(g.url), 1200);
      const set = buildSrcSet(mediaUrl(g.url));
      if (set) l.setAttribute("imagesrcset", set);
      l.setAttribute("imagesizes", "(max-width: 768px) 100vw, 66vw");
      document.head.appendChild(l);
      links.push(l);
    });
    return () => {
      links.forEach((l) => {
        try {
          document.head.removeChild(l);
        } catch {
          /* ignore */
        }
      });
    };
  }, [gallery]);

  return (
    <section
      id="work"
      data-testid="gallery-section"
      className="relative py-20 md:py-40 bg-[#0A0A0A]"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 md:px-14">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-3">
            <p className="num-tick mb-4">{t(UI.section04)}</p>
          </div>
          <div className="col-span-12 md:col-span-9 flex items-end justify-between flex-wrap gap-6">
            <h2 className="font-display uppercase text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9]" data-testid="gallery-title">
              <LineReveal text={t(UI.galleryH1)} />
              <span className="block text-[#1E7FFF] txt-shadow-blue">
                <LineReveal text={t(UI.galleryH2)} delay={0.2} />
              </span>
            </h2>
            <a
              href="#contact"
              className="btn-ghost"
              data-testid="gallery-cta"
            >
              {t(UI.galleryCta)} <ArrowUpRight size={14} />
            </a>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-3 md:gap-4">
          {gallery.map((g, i) => {
            const patterns = [
              "col-span-12 md:col-span-8 aspect-[4/3] md:aspect-[16/10]",
              "col-span-12 md:col-span-4 aspect-[4/3] md:aspect-auto",
              "col-span-6 md:col-span-4 aspect-square",
              "col-span-6 md:col-span-4 aspect-square",
              "col-span-12 md:col-span-4 aspect-[4/3] md:aspect-square",
              "col-span-12 md:col-span-6 aspect-[4/3] md:aspect-[16/10]",
              "col-span-12 md:col-span-6 aspect-[4/3] md:aspect-[16/10]",
            ];
            return (
              <ClipReveal
                key={g.id || i}
                delay={0.05 * (i % 6)}
                className={`relative group overflow-hidden bg-[#111114] ${patterns[i % patterns.length]}`}
              >
                <GalleryItem
                  g={g}
                  index={i}
                  total={gallery.length}
                  caption={t(g.caption)}
                />
              </ClipReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function GalleryItem({ g, index, total, caption }) {
  const [loaded, setLoaded] = React.useState(false);
  const src = optimizeUrl(mediaUrl(g.url), 1200);
  const srcSet = buildSrcSet(mediaUrl(g.url));
  const eager = index < 3;

  return (
    <div className="relative w-full h-full" data-testid={`gallery-item-${index}`}>
      {/* Skeleton shimmer while loading */}
      {!loaded && (
        <div
          className="absolute inset-0 bg-gradient-to-br from-[#141418] via-[#0f0f13] to-[#141418] animate-pulse"
          aria-hidden="true"
        />
      )}
      <motion.img
        src={src}
        srcSet={srcSet}
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        alt={caption || "Work"}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={eager ? "high" : "auto"}
        onLoad={() => setLoaded(true)}
        className="w-full h-full object-cover"
        style={{ opacity: loaded ? 1 : 0 }}
        initial={{ scale: 1.04 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ scale: 1.06 }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-70 group-hover:opacity-100 transition-opacity" />
      <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
        <div>
          <p className="num-tick text-white/70">
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </p>
          <p className="text-white text-sm md:text-base font-body mt-1">
            {caption}
          </p>
        </div>
        <div className="w-9 h-9 border border-white/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all group-hover:bg-[#1E7FFF] group-hover:border-[#1E7FFF]">
          <ArrowUpRight size={16} className="text-white" />
        </div>
      </div>
      <div className="corner-ticks opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}
